import { EventConstants } from "~/assets/constants/ecomApi";
import type { Product } from "~/types/ecom/product/product.type";
import { TrolleyChannel } from "~/types/ecom/trolley/trolley-map";
import type { TrolleyLineItem } from "~/types/ecom/trolley/trolley.type";

// Function to format single or multiple products into the required structure
export const formatEcommerceItems = (products: Product | any[], listName = "Related products", listId = "related_products") => {
  // Ensure products is an array
  const itemsArray = Array.isArray(products) ? products : [products];

  return itemsArray.map((product, index) => ({
    item_id: product.code || "Unknown_ID",
    item_name: product.name || "Unknown_Name",
    affiliation: "Toolstation Store",
    coupon: "",
    discount: product?.prices?.raw.discount_value || 0,
    index: index,
    item_brand: product.brand || "Unknown_Brand",
    item_category: product.taxonomies?.[0]?.[0]?.name || "Unknown_Category",
    item_category2: product.taxonomies?.[0]?.[1]?.name || null,
    item_category3: product.taxonomies?.[0]?.[2]?.name || null,
    item_list_name: listName,
    item_list_id: listId,
    item_variant: product.pack_size || "Unknown_Variant",
    location_id: "",
    price: product?.prices?.raw.gross || 0,
    price_excluding_tax: product?.prices?.raw.net || 0,
    quantity: product.quantity||1 ,
  }));
};
const logEcommerceEventHelper = (eventName: string, eventLabel: string, products: any | any[], extraProperties = {}) => {
  if (!products) return;

  const items = formatEcommerceItems(products);
  logEvent({
    options: {
      eventName,
      eventLabel,
      eventAction: "click",
      eventProperties: {
        ...extraProperties,
      },
      ecommerce: {
        currency: EventConstants.currency,
        value: items.reduce((acc, item) => acc + item.price, 0),
        value_excluding_tax: items.reduce((acc, item) => acc + item.price_excluding_tax, 0),
        item_list_name: "Related products",
        item_list_id: "related_products",
        items,
      },
      eventInteraction: true,
    },
  });
};

// Function to log ecommerce event
export const logEcommerceEvent = (products: any | any[]) => {
  logEcommerceEventHelper("select_item", "select item", products);
};

export const logViewItemList = (products: any | any[]) => {
  logEcommerceEventHelper("view_item_list", "view_item_list", products);
};

export const logViewItems = (products: any | any[]) => {
  logEcommerceEventHelper("view_item", "view_item", products);
};

export const logOutOfStock = (products: any | any[]) => {
  logEcommerceEventHelper("pdp_out_of_stock", "pdp_out_of_stock", products, { eventAction: "page_view" });
};

export const logPdpBottomAddToCart = (products: any | any[]) => {
  logEcommerceEventHelper("pdp_bottom_add_to_cart_bar", "pdp_bottom_add_to_cart_bar", products);
};

export const logPPdpNextDayCollection = (products: any | any[]) => {
  logEcommerceEventHelper("pdp_next_day_collection", "pdp_next_day_collection", products);
};
export const logViewCart = (products: any | any[]) => {
  logEcommerceEventHelper("view_cart", "view_cart", products);
};



export const logPdpProductQty = (products: any | any[], qty: number) => {
  logEcommerceEventHelper("pdp_product_quantity", `quantity ${qty}`, products, {
    product_quantity: qty,
  });
};
export const logAddToCart = (line: TrolleyLineItem ) => {
  const {product, delivery_method_code, quantity }=line;
  if (!product) return;
  const formattedItem = formatEcommerceItems(product);
  formattedItem[0].quantity = quantity;
  logEvent({
    options: {
      eventAction: "click",
      eventProperties: "",
      ecommerce: {
        currency: EventConstants.currency,
        value: product?.prices?.raw.gross,
        value_excluding_tax: product?.prices?.raw.net,
        delivery_method_code,
        item_list_name: "Related products",
        item_list_id: "related_products",
        items: formattedItem
      },
      eventInteraction: true,
      eventLabel: `User action: ${product.code} added to cart`,
      eventName: "add_to_cart",
    },
  });
};



export const logDescriptionAccordionClicks = (products: any | any[], isOpen: boolean, type: string) => {
  const eventName =
    type === "product_description" ? "pdp_description_accordion" :
    type === "technical_specification" ? "pdp_technical_accordion" : "";

  logEcommerceEventHelper(eventName, `On product detail page ${type} accordion clicked`, products, {
    value: isOpen,
  });
};


export const formViewLogger = (formName: string) => {
  logEvent({
    options: {
      eventName: "form_view",
      eventLabel: `form_view ${formName}`,
      eventAction: "page_view",
      eventProperties: {
        form: { form_name: formName },
      },
      eventInteraction: true,
    },
  });
};

export function getFormattedTrolleyDetails({
  trolleyStore,
  details,
}: {
  trolleyStore: any;
  details?: any;
}): Record<string, any> {
  const trolleyDetails = {
    items: [],
    value: 0,
    value_excluding_tax: 0,
    exponeaItems: [],
    exponeaItemsDetails: [],
  };

  if (
    !trolleyStore?.trolley_line_items ||
    Object.keys(trolleyStore.trolley_line_items).length === 0
  ) {
    trolleyDetails.items = "No trolley items exist";
    return trolleyDetails;
  }

  Object.entries(trolleyStore.trolley_line_items).forEach(([channel, items]) => {
    items.forEach((item) => {
      const formattedItem = {
        item_id: item.product_code,
        item_name: item.product?.name,
        item_brand: item.product?.brand,
        item_category: item.product?.taxonomies?.[0]?.[0]?.name ?? "",
        item_category2: item.product?.taxonomies?.[0]?.[1]?.name ?? "",
        item_category3: item.product?.taxonomies?.[0]?.[2]?.name ?? "",
        item_variant: item.product?.pack_size,
        price: item.product?.prices?.raw.gross,
        price_excluding_tax: item.product?.prices?.raw.net,
        quantity: item.quantity??1,
        affiliation: "Toolstaion",
        coupon: "",
        discount: item.product?.prices?.raw.discount_value || 0,
        location_id: details?.id??"",
        location: details??"",
        delivery_method_code: item.delivery_method_code,
      };

      const formattedItem2 = {
        brand: item.product?.brand,
        category: [
          item.product?.taxonomies?.[0]?.[0]?.name ?? "",
          item.product?.taxonomies?.[0]?.[1]?.name ?? "",
          item.product?.taxonomies?.[0]?.[2]?.name ?? ""
        ].filter(Boolean),
        channel: item.product?.channel,
        delivery_method: item.delivery_method_code || "Unknown",
        item_id: item.product_code,
        item_price: item.product?.prices?.raw.gross,
        item_price_without_tax: item.product?.prices?.raw.net,
        quantity: item.quantity,
        slug: item.product?.slug,
        title: item.product?.name,
        total_price: (Number(item.product?.prices?.raw.gross || 0)) * item.quantity,
        total_price_exc_tax: (Number(item.product?.prices?.raw.net || 0)) * item.quantity,
        trolley_channel: TrolleyChannel[item.channel]
      };

      trolleyDetails.items.push(formattedItem);
      trolleyDetails.exponeaItemsDetails.push(formattedItem2);
      trolleyDetails.exponeaItems.push({
        item_id: item.product?.code,
        quantity: item.quantity,
      });

      trolleyDetails.value += Number(item.product?.prices?.raw.gross || 0) * item.quantity;
      trolleyDetails.value_excluding_tax += Number(item.product?.prices?.raw.net || 0) * item.quantity;
    });
  });

  return trolleyDetails;
}

export function trackCheckoutStepEvent({
  trolleyStore,
  checkoutStore,
  stepName,
  stepNumber,
  trolleyDetails,
  guest,
  extras = {}
}: {
  trolleyStore: any;
  checkoutStore: any;
  stepName: string;
  stepNumber: number;
  trolleyDetails: any;
  guest: boolean;
  extras?: Record<string, any>;
}) {
  const exponeaTrackingObject = {
    all_products_for_delivery: trolleyStore.trolley.is_all_lines_delivery,
    currency: EventConstants.currency,
    delivery: checkoutStore.shipping_cost,
    step: stepName,
    step_number: stepNumber,
    discount_value: trolleyStore.total_discount_amount,
    promo_code_value: 0,
    tax_value: trolleyStore.trolley.order_response?.subtotals?.vat.raw,
    total_price: trolleyStore.trolley.order_response?.subtotals?.inc_vat.raw,
    total_price_exc_tax: trolleyStore.trolley.order_response?.subtotals?.ex_vat.raw,
    total_quantity: trolleyStore.total_products,
    product_list: trolleyDetails.exponeaItems,
    product_details: trolleyDetails.exponeaItemsDetails,
    Guest:guest,
    ...extras
  };

  import("~/services/exponeaEvents.service").then(({ trackExponeaEvent }) => {
    trackExponeaEvent("checkout", exponeaTrackingObject);
  });
}
